import React, { useState, useMemo } from 'react';
import { Button, Text, Label } from '@fluentui/react-components';
import { Box, useFluentTokens } from '../../../../theme/fluent';
import type { ChatWidgetProps } from '../../types/widgets';
import type { ApiMultiSelectWidget } from '../../types/api';

export const MultiSelectWidget: React.FC<ChatWidgetProps<ApiMultiSelectWidget>> = ({
  data,
  isDisabled = false,
  onAction,
}) => {
  const { tokens, modeTokens } = useFluentTokens();
  const {
    widget_id,
    title,
    description,
    groups,
    submit_button,
    cancel_button,
  } = data;

  // Initialize selections with default values
  const [selections, setSelections] = useState<Record<string, string[]>>(() => {
    const initialSelections: Record<string, string[]> = {};
    (groups ?? []).forEach((group) => {
      const defaultSelected = group.options
        .filter((opt) => opt.default_selected)
        .map((opt) => opt.option_id);
      initialSelections[group.group_id] = defaultSelected;
    });
    return initialSelections;
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleOptionClick = (groupId: string, optionId: string, selectionMode: 'single' | 'multiple') => {
    if (isDisabled || hasSubmitted) return;
    
    setSelections((prev) => {
      const currentGroupSelections = prev[groupId] || [];
      
      if (selectionMode === 'single') {
        // Single select: toggle or replace
        return {
          ...prev,
          [groupId]: currentGroupSelections.includes(optionId) ? [] : [optionId],
        };
      } else {
        // Multiple select: toggle in array
        return {
          ...prev,
          [groupId]: currentGroupSelections.includes(optionId)
            ? currentGroupSelections.filter((id) => id !== optionId)
            : [...currentGroupSelections, optionId],
        };
      }
    });
  };

  const isSelected = (groupId: string, optionId: string): boolean => {
    return selections[groupId]?.includes(optionId) || false;
  };

  // Validation: check if all required groups have selections
  const canSubmit = useMemo(() => {
    return (groups ?? []).every((group) => {
      if (!group.required) return true;
      const groupSelections = selections[group.group_id] || [];
      return groupSelections.length > 0;
    });
  }, [groups, selections]);

  const handleSubmit = async () => {
    if (!submit_button || !onAction) return;
    
    const selectedOptions: string[] = [];
    Object.values(selections).forEach((groupSelections) => {
      selectedOptions.push(...groupSelections);
    });

    setIsSubmitting(true);
    try {
      await onAction('button_action', {
        widget_id,
        button_action: submit_button.action_value,
        selected_options: selectedOptions,
      });
      setHasSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!cancel_button || !onAction) return;
    
    setIsSubmitting(true);
    try {
      await onAction('button_action', {
        widget_id,
        button_action: cancel_button.action_value,
      });
      setHasSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const effectivelyDisabled = isDisabled || hasSubmitted;

  if (effectivelyDisabled) {
    return (
      <Box
        style={{
          marginTop: tokens.spacingVerticalM,
          padding: tokens.spacingHorizontalL,
          borderRadius: tokens.borderRadiusMedium,
          backgroundColor: modeTokens.colorNeutralBackground2,
          border: `0.5px solid ${modeTokens.colorNeutralStroke2}`,
          display: 'flex',
          flexDirection: 'row',
          gap: tokens.spacingHorizontalL,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        {(groups ?? []).map((group) => {
          const groupSelections = selections[group.group_id] || [];
          
          // If no selections, show "No selection made" message
          if (groupSelections.length === 0) {
            return (
              <Box
                key={group.group_id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: tokens.spacingVerticalS,
                  flex: '0 0 auto',
                }}
              >
                <Text
                  weight="semibold"
                  style={{
                    color: modeTokens.colorNeutralForeground1,
                    fontSize: tokens.fontSizeBase300,
                    lineHeight: tokens.lineHeightBase300,
                    display: 'block',
                  }}
                >
                  {group.group_label}
                </Text>
                <Text
                  style={{
                    color: modeTokens.colorNeutralForeground3,
                    fontSize: tokens.fontSizeBase200,
                    fontStyle: 'italic',
                  }}
                >
                  No selection made
                </Text>
              </Box>
            );
          }

          const selectedOptions = group.options.filter((opt) =>
            groupSelections.includes(opt.option_id)
          );

          return (
            <Box
              key={group.group_id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.spacingVerticalS,
                flex: '0 0 auto',
              }}
            >
              {/* Group Label */}
              <Text
                weight="semibold"
                style={{
                  color: modeTokens.colorNeutralForeground1,
                  fontSize: tokens.fontSizeBase300,
                  lineHeight: tokens.lineHeightBase300,
                  display: 'block',
                }}
              >
                {group.group_label}
              </Text>

              {/* Selected Options as Green Chips */}
              <Box
                display="flex"
                style={{
                  gap: tokens.spacingHorizontalS,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                {selectedOptions.map((option) => (
                  <Box
                    key={option.option_id}
                    style={{
                      borderRadius: tokens.borderRadiusXXXXLarge,
                      border: `1px solid ${modeTokens.colorGreenBorder}`,
                      backgroundColor: modeTokens.colorGreenBackground,
                      color: modeTokens.colorGreenForeground,
                      fontWeight: tokens.fontWeightSemibold,
                      fontSize: tokens.fontSizeBase200,
                      lineHeight: tokens.lineHeightBase200,
                      paddingLeft: tokens.spacingHorizontalM,
                      paddingRight: tokens.spacingHorizontalM,
                      paddingTop: tokens.spacingVerticalXS,
                      paddingBottom: tokens.spacingVerticalXS,
                      display: 'inline-flex',
                      alignItems: 'center',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {option.label}
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box
      style={{
        marginTop: tokens.spacingVerticalM,
        padding: tokens.spacingHorizontalL,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: effectivelyDisabled 
          ? modeTokens.colorNeutralBackground3 
          : modeTokens.colorNeutralBackground2,
        border: `0.5px solid ${modeTokens.colorNeutralStroke2}`,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXL,
        opacity: effectivelyDisabled ? 0.7 : 1,
        pointerEvents: effectivelyDisabled ? 'none' : 'auto',
      }}
    >
      {title && (
        <Text
          as="h3"
          size={500}
          weight="semibold"
          style={{
            display: 'block',
            marginBottom: tokens.spacingVerticalS,
            color: modeTokens.colorNeutralForeground1,
          }}
        >
          {title}
        </Text>
      )}

      {description && (
        <Text
          size={300}
          style={{
            display: 'block',
            marginBottom: tokens.spacingVerticalM,
            color: modeTokens.colorNeutralForeground2,
          }}
        >
          {description}
        </Text>
      )}

      {/* Groups */}
      <Box display="flex" style={{ flexDirection: 'column', gap: tokens.spacingHorizontalL }}>
        {(groups ?? []).map((group) => (
          <Box key={group.group_id} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
            <Label
              size="medium"
              weight="semibold"
              required={group.required}
              style={{
                color: modeTokens.colorNeutralForeground1,
                fontSize: tokens.fontSizeBase300,
                lineHeight: tokens.lineHeightBase300,
                paddingBottom: tokens.spacingVerticalXS,
              }}
            >
              {group.group_label}
            </Label>

            {/* Options as chips */}
            <Box
              display="flex"
              style={{
                gap: tokens.spacingHorizontalS,
                flexWrap: 'wrap',
              }}
            >
              {group.options.map((option) => {
                const selected = isSelected(group.group_id, option.option_id);
                return (
                  <Button
                    key={option.option_id}
                    appearance="subtle"
                    size="small"
                    onClick={() =>
                      handleOptionClick(group.group_id, option.option_id, group.selection_mode)
                    }
                    style={{
                      borderRadius: tokens.borderRadiusXXXXLarge,
                      border: `1px solid ${selected ? modeTokens.colorGreenBorder : modeTokens.colorNeutralStroke1}`,
                      backgroundColor: selected ? modeTokens.colorGreenBackground : 'transparent',
                      color: selected ? modeTokens.colorGreenForeground : modeTokens.colorNeutralForeground2,
                      fontWeight: selected ? tokens.fontWeightSemibold : tokens.fontWeightRegular,
                      fontSize: tokens.fontSizeBase200,
                      lineHeight: tokens.lineHeightBase200,
                      paddingLeft: tokens.spacingHorizontalM,
                      paddingRight: tokens.spacingHorizontalM,
                      paddingTop: tokens.spacingVerticalXS,
                      paddingBottom: tokens.spacingVerticalXS,
                      minWidth: 'auto',
                    }}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>

      <Box display="flex" style={{ gap: tokens.spacingHorizontalM }}>
        {submit_button && (
          <Button
            appearance={canSubmit && !isSubmitting && !effectivelyDisabled ? 'primary' : 'secondary'}
            disabled={!canSubmit || isSubmitting || effectivelyDisabled}
            onClick={handleSubmit}
            style={{
              borderRadius: tokens.borderRadiusXXXXLarge,
              fontWeight: tokens.fontWeightSemibold,
              fontSize: tokens.fontSizeBase300,
              lineHeight: tokens.lineHeightBase300,
              paddingLeft: tokens.spacingHorizontalM,
              paddingRight: tokens.spacingHorizontalM,
              paddingTop: tokens.spacingVerticalXS,
              paddingBottom: tokens.spacingVerticalXS,
              ...((!canSubmit || isSubmitting || effectivelyDisabled) && {
                backgroundColor: modeTokens.colorNeutralBackground3,
                color: modeTokens.colorNeutralForeground1Disabled,
                border: 'none',
              }),
            }}
          >
            {isSubmitting ? 'Submitting...' : effectivelyDisabled ? 'Submitted' : submit_button.label}
          </Button>
        )}

        {cancel_button && (
          <Button
            appearance="secondary"
            disabled={isSubmitting || effectivelyDisabled}
            onClick={handleCancel}
            style={{
              borderRadius: tokens.borderRadiusXXXXLarge,
              fontWeight: tokens.fontWeightSemibold,
              fontSize: tokens.fontSizeBase300,
              lineHeight: tokens.lineHeightBase300,
              paddingLeft: tokens.spacingHorizontalM,
              paddingRight: tokens.spacingHorizontalM,
              paddingTop: tokens.spacingVerticalXS,
              paddingBottom: tokens.spacingVerticalXS,
            }}
          >
            {cancel_button.label}
          </Button>
        )}
      </Box>
    </Box>
  );
};
